import { savedLocally } from "./mutationResult.js";

function completedNotification(
  message: string,
  title: string,
  result?: unknown,
) {
  const local =
    savedLocally(result) ||
    (typeof navigator !== "undefined" && navigator.onLine === false);
  return {
    message: local
      ? "Saved locally. Changes will sync when a connection is available."
      : message,
    type: local ? "info" : "success",
    options: {
      title: local ? "Saved locally" : title,
      variant: local ? "info" : "success",
      showProgress: true,
      duration: 3000,
    },
  };
}

/**
 * Comprehensive notification helpers for consistent notifications across the application
 * These are helper functions that work with the useNotification hook
 */

/**
 * Form save notifications
 */
export const notificationHelpers = {
  // === FORM OPERATIONS ===
  actionCreate: (actionName: string, result?: unknown) =>
    completedNotification(
      actionName + " created successfully",
      "Create Successful",
      result,
    ),

  actionDelete: (actionName: string, result?: unknown) =>
    completedNotification(
      actionName + " deleted successfully",
      "Delete Successful",
      result,
    ),

  actionSaveError: (formName: string, error?: string) => ({
    message: `Failed to save ${formName}${error ? `: ${error}` : ""}`,
    type: "error",
    options: {
      title: "Save Failed",
      variant: "error",
      showProgress: true,
      duration: 5000,
    },
  }),

  actionCreateError: (itemName: string, error?: string) => ({
    message: `Failed to create ${itemName}${error ? `: ${error}` : ""}`,
    type: "error",
    options: {
      title: "Creation Failed",
      variant: "error",
      showProgress: true,
      duration: 5000,
    },
  }),

  actionDeleteError: (itemName: string, error?: string) => ({
    message: `Failed to delete ${itemName}${error ? `: ${error}` : ""}`,
    type: "error",
    options: {
      title: "Deletion Failed",
      variant: "error",
      showProgress: true,
      duration: 5000,
    },
  }),

  // === FORM OPERATIONS ===
  formSaveSuccess: (formName: string, result?: unknown) =>
    completedNotification(
      formName + " saved successfully",
      "Save Successful",
      result,
    ),

  formSaveError: (formName: string, error?: string) => ({
    message: `Failed to save ${formName}${error ? `: ${error}` : ""}`,
    type: "error",
    options: {
      title: "Save Failed",
      variant: "error",
      showProgress: true,
      duration: 5000,
    },
  }),

  formDeleteSuccess: (itemName: string, result?: unknown) =>
    completedNotification(
      itemName + " deleted successfully",
      "Deletion Successful",
      result,
    ),

  formDeleteError: (itemName: string, error?: string) => ({
    message: `Failed to delete ${itemName}${error ? `: ${error}` : ""}`,
    type: "error",
    options: {
      title: "Deletion Failed",
      variant: "error",
      showProgress: true,
      duration: 5000,
    },
  }),

  // === FILE/IMAGE OPERATIONS ===
  imageUploadSuccess: (fileName: string, result?: unknown) =>
    completedNotification(
      "Image " + fileName + " uploaded successfully",
      "Upload Successful",
      result,
    ),

  imageUploadError: (fileName: string, error?: string) => ({
    message: `Failed to upload ${fileName}${error ? `: ${error}` : ""}`,
    type: "error",
    options: {
      title: "Upload Failed",
      variant: "error",
      showProgress: true,
      duration: 5000,
    },
  }),

  imageDeleteSuccess: (fileName: string, result?: unknown) =>
    completedNotification(
      "Image " + fileName + " removed successfully",
      "Deletion Successful",
      result,
    ),

  imageDeleteError: (fileName: string, error?: string) => ({
    message: `Failed to delete image ${fileName}${error ? `: ${error}` : ""}`,
    type: "error",
    options: {
      title: "Deletion Failed",
      variant: "error",
      showProgress: true,
      duration: 5000,
    },
  }),

  imageDescriptionUpdated: () => ({
    message: "Image description updated and will be saved automatically",
    type: "info",
    options: {
      title: "Description Updated",
      variant: "info",
      showProgress: true,
      duration: 2000,
    },
  }),

  // === VALIDATION ERRORS ===
  validationError: (fieldName: string, message: string) => ({
    message: `${fieldName}: ${message}`,
    type: "error",
    options: {
      title: "Validation Error",
      variant: "error",
      showProgress: true,
      duration: 5000,
    },
  }),

  requiredFieldMissing: (fieldName: string) => ({
    message: `${fieldName} is required`,
    type: "error",
    options: {
      title: "Required Field Missing",
      variant: "error",
      showProgress: true,
      duration: 4000,
    },
  }),

  invalidFormat: (fieldName: string, format: string) => ({
    message: `${fieldName} must be in ${format} format`,
    type: "error",
    options: {
      title: "Invalid Format",
      variant: "error",
      showProgress: true,
      duration: 4000,
    },
  }),

  // === REPORT OPERATIONS ===
  reportGenerationStarted: () => ({
    message: "Generating report, please wait...",
    type: "info",
    options: {
      title: "Generating Report",
      variant: "info",
      showProgress: false,
      duration: 0, // Persistent until manually dismissed
    },
  }),

  reportGenerationSuccess: (reportType: string) => ({
    message: `${reportType} report generated and downloaded successfully`,
    type: "success",
    options: {
      title: "Report Generated",
      variant: "success",
      showProgress: true,
      duration: 4000,
    },
  }),

  reportGenerationError: (error?: string) => ({
    message: `Failed to generate report${error ? `: ${error}` : ""}`,
    type: "error",
    options: {
      title: "Report Generation Failed",
      variant: "error",
      showProgress: true,
      duration: 5000,
    },
  }),

  // === VERSION OPERATIONS ===
  versionSaveSuccess: (versionNumber: number) => ({
    message: `Version ${versionNumber} saved successfully`,
    type: "success",
    options: {
      title: "Version Saved",
      variant: "success",
      showProgress: true,
      duration: 3000,
    },
  }),

  versionSaveError: (error?: string) => ({
    message: `Failed to save version${error ? `: ${error}` : ""}`,
    type: "error",
    options: {
      title: "Version Save Failed",
      variant: "error",
      showProgress: true,
      duration: 5000,
    },
  }),

  versionRestored: (versionNumber: number) => ({
    message: `Restored to version ${versionNumber}`,
    type: "success",
    options: {
      title: "Version Restored",
      variant: "success",
      showProgress: true,
      duration: 3000,
    },
  }),

  // === APPROVAL OPERATIONS ===
  approvalSubmitted: () => ({
    message: "Submission sent for approval",
    type: "success",
    options: {
      title: "Approval Requested",
      variant: "success",
      showProgress: true,
      duration: 3000,
    },
  }),

  approvalApproved: () => ({
    message: "Request approved successfully",
    type: "success",
    options: {
      title: "Approved",
      variant: "success",
      showProgress: true,
      duration: 3000,
    },
  }),

  approvalRejected: (reason?: string) => ({
    message: `Request rejected${reason ? `: ${reason}` : ""}`,
    type: "error",
    options: {
      title: "Rejected",
      variant: "error",
      showProgress: true,
      duration: 5000,
    },
  }),

  // === AUTOSAVE NOTIFICATIONS ===
  autoSaveStarted: () => ({
    message: "Auto-saving changes...",
    type: "info",
    options: {
      title: "Saving",
      variant: "info",
      showProgress: false,
      duration: 0, // Persistent
    },
  }),

  autoSaveSuccess: (itemName: string) => ({
    message: `${itemName} auto-saved successfully`,
    type: "success",
    options: {
      title: "Auto-saved",
      variant: "success",
      showProgress: true,
      duration: 2000,
    },
  }),

  autoSaveError: (itemName: string) => ({
    message: `Failed to auto-save ${itemName}`,
    type: "error",
    options: {
      title: "Auto-save Failed",
      variant: "error",
      showProgress: true,
      duration: 5000,
    },
  }),

  unsavedChanges: () => ({
    message: "You have unsaved changes",
    type: "warning",
    options: {
      title: "Unsaved Changes",
      variant: "warning",
      showProgress: true,
      duration: 3000,
    },
  }),

  // === GENERAL OPERATIONS ===
  operationSuccess: (operation: string) => ({
    message: `${operation} completed successfully`,
    type: "success",
    options: {
      title: "Success",
      variant: "success",
      showProgress: true,
      duration: 3000,
    },
  }),

  operationError: (operation: string, error?: string) => ({
    message: `${operation} failed${error ? `: ${error}` : ""}`,
    type: "error",
    options: {
      title: "Operation Failed",
      variant: "error",
      showProgress: true,
      duration: 5000,
    },
  }),

  loadingData: () => ({
    message: "Loading data, please wait...",
    type: "info",
    options: {
      title: "Loading",
      variant: "info",
      showProgress: false,
      duration: 0, // Persistent
    },
  }),

  dataLoadingComplete: () => ({
    message: "Data loaded successfully",
    type: "success",
    options: {
      title: "Loaded",
      variant: "success",
      showProgress: true,
      duration: 2000,
    },
  }),

  noChangesDetected: () => ({
    message: "No changes to save",
    type: "info",
    options: {
      title: "No Changes",
      variant: "info",
      showProgress: true,
      duration: 2000,
    },
  }),

  // === PERMISSION ERRORS ===
  permissionDenied: () => ({
    message: "You don't have permission to perform this action",
    type: "error",
    options: {
      title: "Permission Denied",
      variant: "error",
      showProgress: true,
      duration: 5000,
    },
  }),

  readOnlyMode: () => ({
    message: "This form is in read-only mode",
    type: "warning",
    options: {
      title: "Read-Only Mode",
      variant: "warning",
      showProgress: true,
      duration: 2000,
    },
  }),

  // === NETWORK ERRORS ===
  networkError: () => ({
    message: "Network error. Please check your connection and try again",
    type: "error",
    options: {
      title: "Network Error",
      variant: "error",
      showProgress: true,
      duration: 5000,
    },
  }),

  serverError: (statusCode?: number) => ({
    message: `Server error${statusCode ? ` (${statusCode})` : ""}. Please try again later`,
    type: "error",
    options: {
      title: "Server Error",
      variant: "error",
      showProgress: true,
      duration: 5000,
    },
  }),
};

/**
 * Hook to notify users with the prepared notification objects
 * Usage: const notification = notificationHelpers.formSaveSuccess("Project Data");
 *        notify(notification.message, notification.type, notification.options);
 */
export function getNotificationConfig(notification: any) {
  return {
    message: notification.message,
    type: notification.type,
    options: notification.options || {},
  };
}
